import React, { useState } from 'react';
import { Segment, Header, Icon, Label, Button, SemanticCOLORS } from 'semantic-ui-react';

/**
 * Verdict from the dedup pipeline (Plan 001 Phase 1).
 *
 * Mirrors the enum stored in `FeedPosts.LastDuplicateCheckVerdict` —
 * keep this list in sync if the backend taxonomy changes.
 */
export type DedupVerdict =
    | 'duplicate'
    | 'update'
    | 'related_distinct'
    | 'unrelated'
    | 'insufficient_evidence';

interface DedupAuditPanelProps {
    verdict: string | null;
    confidence: number | null;
    rationale: string | null;
    /**
     * JSON-encoded structured comparison from the LLM. Rendered
     * pretty-printed inside the expandable detail panel.
     */
    payloadJson: string | null;
}

const VERDICT_LABELS: Record<DedupVerdict, string> = {
    duplicate: 'Duplicate',
    update: 'Update of an existing post',
    related_distinct: 'Related but distinct',
    unrelated: 'Unrelated',
    insufficient_evidence: 'Insufficient evidence',
};

const VERDICT_COLORS: Record<DedupVerdict, SemanticCOLORS> = {
    duplicate: 'red',
    update: 'blue',
    related_distinct: 'teal',
    unrelated: 'green',
    insufficient_evidence: 'yellow',
};

const isKnownVerdict = (v: string | null): v is DedupVerdict =>
    v === 'duplicate' ||
    v === 'update' ||
    v === 'related_distinct' ||
    v === 'unrelated' ||
    v === 'insufficient_evidence';

/**
 * Format a 0.0–1.0 confidence number as a percentage. Bands are
 * paired with Phase 1's auto-mark / auto-reject thresholds:
 * - ≥ 95% → high (auto-marked when verdict is `duplicate`)
 * - 60–94% → mid (abstention band — no flag set even if verdict says duplicate)
 * - < 60% → low (auto-rejected)
 */
const formatConfidence = (confidence: number | null): string => {
    if (confidence === null || Number.isNaN(confidence)) return '—';
    return `${(confidence * 100).toFixed(1)}%`;
};

const confidenceColor = (confidence: number | null): SemanticCOLORS => {
    if (confidence === null) return 'grey';
    if (confidence >= 0.95) return 'red';
    if (confidence >= 0.6) return 'yellow';
    return 'green';
};

/**
 * Try to pretty-print the structured payload. Falls back to the raw
 * string if it isn't valid JSON (defensive — backend always emits JSON).
 */
const formatPayload = (raw: string): string => {
    try {
        const parsed = JSON.parse(raw);
        return JSON.stringify(parsed, null, 2);
    } catch {
        return raw;
    }
};

/**
 * DedupAuditPanel — surfaces the per-check audit fields (Plan 001 Phase 2)
 * inside the eventsAdmin EventForm so editors can audit the dedup
 * pipeline's decision: what verdict, what confidence, what rationale,
 * and (collapsibly) the full structured comparison the LLM produced.
 *
 * Renders nothing when the post predates audit logging (all fields null).
 */
export const DedupAuditPanel: React.FC<DedupAuditPanelProps> = ({
    verdict,
    confidence,
    rationale,
    payloadJson,
}) => {
    const [expanded, setExpanded] = useState(false);

    // Hide entirely when there's no audit data on the row.
    if (!verdict && confidence === null && !rationale && !payloadJson) {
        return null;
    }

    const verdictLabel = isKnownVerdict(verdict)
        ? VERDICT_LABELS[verdict]
        : verdict ?? 'Unknown';
    const verdictColor = isKnownVerdict(verdict)
        ? VERDICT_COLORS[verdict]
        : 'grey';

    return (
        <Segment
            data-testid="dedup-audit-panel"
            style={{ marginTop: '0.75rem', background: '#fafafa' }}
        >
            <Header as="h5" style={{ marginBottom: '0.5rem' }}>
                <Icon name="search" />
                <Header.Content>
                    Dedup audit
                    <Header.Subheader>
                        From the most recent ingest check.
                    </Header.Subheader>
                </Header.Content>
            </Header>

            <div style={{ marginBottom: '0.5rem' }}>
                <Label
                    color={verdictColor}
                    data-testid="dedup-audit-verdict"
                    style={{ marginRight: '0.5rem' }}
                >
                    {verdictLabel}
                </Label>
                <Label
                    color={confidenceColor(confidence)}
                    data-testid="dedup-audit-confidence"
                >
                    Confidence: {formatConfidence(confidence)}
                </Label>
            </div>

            {rationale && (
                <p
                    data-testid="dedup-audit-rationale"
                    style={{ marginBottom: '0.5rem', color: '#444' }}
                >
                    <strong>Reason:</strong> {rationale}
                </p>
            )}

            {payloadJson && (
                <>
                    <Button
                        type="button"
                        size="mini"
                        basic
                        onClick={() => setExpanded((v) => !v)}
                        data-testid="dedup-audit-toggle"
                    >
                        <Icon name={expanded ? 'caret down' : 'caret right'} />
                        {expanded ? 'Hide' : 'Show'} structured comparison
                    </Button>
                    {expanded && (
                        <pre
                            data-testid="dedup-audit-payload"
                            style={{
                                marginTop: '0.5rem',
                                padding: '0.75rem',
                                background: '#f0f0f0',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                overflowX: 'auto',
                                fontSize: '0.85em',
                                maxHeight: '320px',
                            }}
                        >
                            {formatPayload(payloadJson)}
                        </pre>
                    )}
                </>
            )}
        </Segment>
    );
};

export default DedupAuditPanel;
